/**
 * ActionCards component
 *
 * Displays the main action card for editing schedules and links,
 * including advanced search, group management, package creation,
 * and access to various related reports and change history.
 *
 * Also renders additional grouped action cards (RestActions) below the main block.
 */

import { FC, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, DropdownButton, Divider, ThemeStore } from "@foris/avocado-suite";
import { ContextEDH } from "@context/ContextEDH";
import * as mouseflow from "@utils/mouseflow";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import Search from "../search";
import RestActions from "../rest-actions";
import SectionBox from "../section-box";
import useBookingAccess from "@common/hooks/useBookingAccess";
import useGetModulesUrls from "@common/hooks/useGetModulesUrls";
import TourTooltipBeacon from "@common/components/tour-tooltip-beacon";
import OnboardingVideoModal from "@common/components/onboarding-video-modal";
import {
  BOOKINGS_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";
import css from "./actionCards.module.scss";

const ActionCards: FC = () => {
  const { state } = useContext(ContextEDH);
  const { t } = useTranslation();
  const history = useHistory();
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const base = state?.base?.base;
  const userCanAccessToGroupsManager = base?.user?.abilities?.can_access_group_manager;
  const userCanCreate = base?.user?.permissions?.create;

  const { isBookingAccesible, bookingModuleUrl } = useBookingAccess();
  const { isEdhAccesible, canAccessEDH: canSeeEdhLinks } = useGetModulesUrls();

  const showHotspot = useMemo(() => {
    return shouldShowHotspot(BOOKINGS_ONBOARDING);
  }, [onboardingsSeen]);

  const userCanEditLinkSection = useMemo(() => {
    const abilities = base?.user?.abilities;
    const createGroupWithinLinkCreation = state?.base?.createGroupWithinLinkCreation;

    if (createGroupWithinLinkCreation === "MANDATORY") {
      return abilities?.can_create_groups_in_bundle_view && abilities?.can_edit_links_and_sections;
    }

    return abilities?.can_edit_links_and_sections;
  }, [base?.user, state?.base?.createGroupWithinLinkCreation]);

  // Navigation URLs used in the buttons and dropdowns
  const {
    changeHistoryUrl,
    linkChangeHistoryUrl,
    groupsManagerUrl,
    createSectionUrl,
    createPackageUrl,
  } = useNavigationUrl();

  const historyChangeOptions = useMemo(() => {
    const options = [
      {
        label: t("home.action-cards.editing-schedules.dropdown-button-changelog.btn-link"),
        onClick: () => history.push(linkChangeHistoryUrl()),
      },
    ];

    if (userCanAccessToGroupsManager) {
      options.unshift({
        label: t("home.action-cards.editing-schedules.dropdown-button-changelog.btn-group"),
        onClick: () => {
          mouseflow.actionTag(
            "action_groups_manager_main_change_history",
            state?.base?.isMouseflowEnabled,
          );
          history.push(changeHistoryUrl());
        },
      });
    }

    return options;
  }, [userCanAccessToGroupsManager, t, history, changeHistoryUrl, linkChangeHistoryUrl]);

  return (
    <div className={css.actionCards}>
      {showHotspot && (
        <OnboardingVideoModal
          isOpen={showVideoModal}
          title={t("onboarding.bookings.title")}
          description={t("onboarding.bookings.description")}
          videoUrl="https://files.foris.ai/darwin/onboarding/Reservas+-+PROD.mp4"
          onClose={() => {
            setShowVideoModal(false);
            setIsOnboardingOpen(false, BOOKINGS_ONBOARDING);
          }}
        />
      )}

      <div className={css.actionCards_main}>
        {isEdhAccesible && (
          <SectionBox
            icon="calendar-edh"
            title={<>{t("home.action-cards.editing-schedules.title")}</>}
            description={t("home.action-cards.editing-schedules.description")}
            customContent={
              canSeeEdhLinks && (
                <>
                  <Search />
                  <Divider className={css.actionCards_divider} />
                </>
              )
            }
            buttons={
              <>
                {userCanCreate &&
                  (userCanEditLinkSection || !!base?.user?.abilities?.can_edit_packages) &&
                  canSeeEdhLinks && (
                    <div className={css.creationButtons}>
                      {userCanCreate && userCanEditLinkSection && (
                        <Button leftIcon="plus" onClick={() => history.push(createSectionUrl())}>
                          {t("home.action-cards.editing-schedules.btn-create-link")}
                        </Button>
                      )}

                      {userCanCreate && !!base?.user?.abilities?.can_edit_packages && (
                        <Button leftIcon="plus" onClick={() => history.push(createPackageUrl())}>
                          {t("home.action-cards.editing-schedules.btn-create-package")}
                        </Button>
                      )}
                    </div>
                  )}

                {userCanAccessToGroupsManager && canSeeEdhLinks && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      mouseflow.actionTag("action_groups_manager", state?.base?.isMouseflowEnabled);
                      history.push(groupsManagerUrl());
                    }}
                  >
                    {t("home.action-cards.editing-schedules.btn-group-management")}
                  </Button>
                )}

                {!!bookingModuleUrl && isBookingAccesible && (
                  <div className={css.bookingButtonContainer}>
                    <Button variant="secondary" onClick={() => history.push(bookingModuleUrl)}>
                      {t("home.action-cards.programming-management.btn-reservations")}
                    </Button>

                    {showHotspot && (
                      <div
                        className={css.tourTooltipBeacon}
                        onClick={() => {
                          setShowVideoModal(true);
                          setIsOnboardingOpen(true, BOOKINGS_ONBOARDING);
                        }}
                      >
                        <TourTooltipBeacon mode={themeMode} />
                      </div>
                    )}
                  </div>
                )}

                {/* Dropdown for change history actions */}
                {canSeeEdhLinks && (
                  <DropdownButton
                    className={css.changeLogSelector}
                    variant="secondary"
                    items={historyChangeOptions}
                  >
                    {t("home.action-cards.editing-schedules.dropdown-button-changelog.title")}
                  </DropdownButton>
                )}
              </>
            }
          />
        )}

        {!isEdhAccesible && isBookingAccesible && !!bookingModuleUrl && (
          <SectionBox
            icon="calendar-edh"
            title={t("home.action-cards.bookings.title")}
            description={t("home.action-cards.bookings.description")}
            buttons={
              <>
                <Button variant="secondary" onClick={() => history.push(bookingModuleUrl)}>
                  {t("home.action-cards.bookings.btn-bookings")}
                </Button>
              </>
            }
          />
        )}
      </div>

      {/* Additional grouped action cards */}
      <RestActions />
    </div>
  );
};

export default ActionCards;
